import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Loader2 } from "lucide-react";
import { searchPlaces, CITY_PRESETS } from "@/lib/geo";

export function LocationInput({ label, value, onChange, placeholder, testid, required }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const search = (q) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.length < 3) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchPlaces(q);
        setResults(results);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange({ text: v });
    setOpen(true);
    search(v);
  };

  const pick = (r) => {
    const text = r.display_name;
    setQuery(text);
    setOpen(false);
    onChange({ text, lat: parseFloat(r.lat), lng: parseFloat(r.lon) });
  };

  // While the query is short, show the built-in list of Indian cities; longer queries use live geocoder results
  // (preset matches stay visible as an instant fallback until results arrive, avoiding dropdown flicker).
  const shortQuery = query.trim().length < 3;
  const presetMatches = CITY_PRESETS.filter((c) => !query.trim() || c.display_name.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 10);
  const shown = shortQuery || results.length === 0 ? presetMatches : results;

  return (
    <div className="space-y-1.5" ref={boxRef}>
      {label && <Label className="text-sm font-medium text-slate-700">{label}{required && <span className="text-rose-500"> *</span>}</Label>}
      <div className="relative">
        <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          data-testid={testid}
          className="pl-9 rounded-xl"
          value={query}
          placeholder={placeholder || "Search an Indian city or address"}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
        {open && shown.length > 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-auto pm-scroll">
            {shortQuery && <p className="px-3 pt-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Popular Indian cities</p>}
            {shown.map((r) => (
              <button
                type="button"
                key={r.place_id}
                data-testid={`${testid}-option`}
                onClick={() => pick(r)}
                className="w-full text-left px-3 py-2.5 text-sm hover:bg-accent flex items-start gap-2 border-b border-slate-100 last:border-0"
              >
                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-slate-700 line-clamp-2">{r.display_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
